import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  type SyndicatorQuestions,
  type SyndicatorQuestionsV2Block,
} from "@lib/config/types";
import { FormField } from "./form/field";
import { FormReviewTopics } from "./form/reviewtopics";
import { type CSSProperties } from "react";
import {
  alignStyle,
  type FontFamily,
  resolveFontFamilyVariables,
  textBlockWidth,
} from "@lib/utils";
import { syndicatorQsFields } from "@lib/syndicator-questions";
import {
  getReviewTopicsForCampaign,
  type ReviewTopic,
  type SmartPromptsOverride,
} from "@lib/smart-prompts";
import { IsPreviewUrl } from "@/routes/preview";
import { apiBaseUrl, apiHeaders } from "@lib/client";
import { logger } from "@lib/logger.client";
import { useFormContext, useWatch } from "react-hook-form";

// SMART_PROMPTS_AB_TEST_OVERRIDE_TEMP_CODE_START
function readSmartPromptsOverride(): SmartPromptsOverride {
  if (typeof window === "undefined") return undefined;
  const value = new URLSearchParams(window.location.search).get(
    "smartPromptsOverride",
  );
  return value === "on" || value === "off" ? value : undefined;
}
// SMART_PROMPTS_AB_TEST_OVERRIDE_TEMP_CODE_END

export {
  MIN_CHARS_FOR_MATCH,
  MATCH_INPUT_DEBOUNCE_MS,
} from "@lib/smart-prompts-constants";
import {
  MIN_CHARS_FOR_MATCH,
  MATCH_INPUT_DEBOUNCE_MS,
} from "@lib/smart-prompts-constants";

function areMatchedIndexesEqual(left: number[], right: number[]): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

type MatchPhrasesResponse = {
  matchedIndexes: number[];
  matchedPhrases?: string[];
};

function isMatchPhrasesResponse(value: unknown): value is MatchPhrasesResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as {
    matchedIndexes?: unknown;
    matchedPhrases?: unknown;
  };

  return (
    Array.isArray(candidate.matchedIndexes) &&
    candidate.matchedIndexes.every((index) => typeof index === "number") &&
    (candidate.matchedPhrases === undefined ||
      (Array.isArray(candidate.matchedPhrases) &&
        candidate.matchedPhrases.every((phrase) => typeof phrase === "string")))
  );
}

function useReviewPhraseMatching(
  topics: ReadonlyArray<ReviewTopic>,
  text: string,
  onMatch: (matchedIndexes: number[]) => void,
  inPreview: boolean,
) {
  const pendingMatchTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pendingMatchRequest = useRef<AbortController | null>(null);
  const latestRequestId = useRef(0);

  const runMatch = useCallback(
    async (nextText: string) => {
      const trimmedText = nextText.trim();
      const requestId = latestRequestId.current + 1;
      latestRequestId.current = requestId;

      if (topics.length === 0) {
        return;
      }

      pendingMatchRequest.current?.abort();
      const abortController = new AbortController();
      pendingMatchRequest.current = abortController;

      try {
        const res = await fetch(`${apiBaseUrl}/match-phrases`, {
          method: "POST",
          headers: {
            ...apiHeaders,
            "Content-Type": "application/json",
            ...(inPreview ? { "X-Preview": "true" } : {}), //X-Preview header is used to indicate to the server that this request is coming from the builder not public facing
          },
          body: JSON.stringify({ text: trimmedText, phrases: topics }),
          signal: abortController.signal,
        });
        if (!res.ok) {
          logger.warn(
            { status: res.status },
            "[smart_prompts] phrase match request failed",
          );
          return;
        }
        const body: unknown = await res.json();
        if (!isMatchPhrasesResponse(body)) {
          logger.warn(body, "[smart_prompts] unexpected response shape");
          return;
        }
        const { matchedIndexes } = body;
        if (requestId !== latestRequestId.current) {
          return;
        }
        onMatch(matchedIndexes);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
        if (requestId !== latestRequestId.current) {
          return;
        }
        logger.warn(err, "[smart_prompts] phrase match failed");
      } finally {
        if (pendingMatchRequest.current === abortController) {
          pendingMatchRequest.current = null;
        }
      }
    },
    [onMatch, topics, inPreview],
  );

  useEffect(() => {
    const trimmedText = text.trim();

    if (topics.length === 0) {
      latestRequestId.current += 1;
      pendingMatchRequest.current?.abort();
      onMatch([]);
      return;
    }

    if (trimmedText.length < MIN_CHARS_FOR_MATCH) {
      latestRequestId.current += 1;
      pendingMatchRequest.current?.abort();
      if (pendingMatchTimeout.current) {
        clearTimeout(pendingMatchTimeout.current);
        pendingMatchTimeout.current = null;
      }
      onMatch([]);
      return;
    }

    if (pendingMatchTimeout.current) {
      clearTimeout(pendingMatchTimeout.current);
    }

    pendingMatchTimeout.current = setTimeout(() => {
      pendingMatchTimeout.current = null;
      void runMatch(trimmedText);
    }, MATCH_INPUT_DEBOUNCE_MS);

    return () => {
      pendingMatchRequest.current?.abort();
      if (pendingMatchTimeout.current) {
        clearTimeout(pendingMatchTimeout.current);
      }
    };
  }, [onMatch, topics, runMatch, text]);
}
type SyndicatorQuestionsProps = SyndicatorQuestionsV2Block & {
  category?: string;
  subcategory?: string;
  orderId?: string;
  isPremium?: boolean;
};

type SyndicatorQuestionsStyles = CSSProperties & {
  "--label-font-name": string;
  "--label-font-weight": string;
  "--label-font-style": string;
  "--label-color": string;
  "--label-font-size": string;
  "--input-font-name": string;
  "--input-font-weight": string;
  "--input-font-style": string;
  "--input-color": string;
  "--input-background": string;
  "--input-border-color": string;
  "--placeholder-color": string;
  "--field-border-size": string;
  "--field-border-top-left-radius": string;
  "--field-border-top-right-radius": string;
  "--field-border-bottom-right-radius": string;
  "--field-border-bottom-left-radius": string;
};

export function SyndicatorQuestions({
  syndicator_questions,
  category,
  subcategory,
  orderId,
  isPremium,
}: SyndicatorQuestionsProps) {
  const { control } = useFormContext();
  const [matchedIndexes, setMatchedIndexes] = useState<number[]>([]);
  const reviewText =
    (useWatch({
      control,
      name: "syndicator-question:review",
    }) as string | undefined) ?? "";

  const handleMatch = useCallback((nextMatchedIndexes: number[]) => {
    setMatchedIndexes((currentMatchedIndexes) =>
      areMatchedIndexesEqual(currentMatchedIndexes, nextMatchedIndexes)
        ? currentMatchedIndexes
        : nextMatchedIndexes,
    );
  }, []);

  const inPreview = typeof window !== "undefined" && IsPreviewUrl();
  // TODO isPremium is a temporary workaround pending implementation of a features service
  // Once features are implemented, we should revisit this code and the db call to the licence table
  const ShowTopics = isPremium === true;
  const override = readSmartPromptsOverride();
  const topics =
    category && subcategory
      ? getReviewTopicsForCampaign(
          ShowTopics,
          category,
          subcategory,
          orderId,
          override,
        )
      : null;

  useReviewPhraseMatching(topics ?? [], reviewText, handleMatch, inPreview);

  const labelFontFamily = syndicator_questions.label_style.font as FontFamily;
  const inputFontFamily = syndicator_questions.input_style.font as FontFamily;

  const transformedFields = syndicatorQsFields(syndicator_questions.fields);

  const reviewTopicsField = transformedFields.find(
    (f): f is typeof f & { topics: string[] } =>
      f.name === "syndicator-question:review_topics",
  );

  const isPill =
    syndicator_questions.input_style.border.radius === 9999.0 ||
    syndicator_questions.input_style.border.radius === 99999.0;

  return (
    <div
      className="grid gap-8"
      style={
        {
          maxWidth: textBlockWidth(syndicator_questions.width),
          ...alignStyle(syndicator_questions),
          ...resolveFontFamilyVariables(
            "--label",
            labelFontFamily,
            syndicator_questions.label_style.font_properties,
          ),
          ...resolveFontFamilyVariables(
            "--input",
            inputFontFamily,
            syndicator_questions.input_style.font_properties,
          ),
          "--label-color": syndicator_questions.label_style.color,
          "--label-font-size": `${(syndicator_questions.label_style as unknown as Record<string, number>).font_size ?? 16}px`,
          "--input-color": syndicator_questions.input_style.color,
          "--input-background":
            syndicator_questions.input_style.background_color,
          "--input-border-color": syndicator_questions.input_style.border.color,
          "--placeholder-color":
            syndicator_questions.input_style.placeholder_color,
          "--field-border-size": `${syndicator_questions.input_style.border.width}px`,
          "--field-border-top-left-radius": `${isPill ? "9999em" : `min(${syndicator_questions.input_style.border.radius}%, 1.5625rem)`}`,
          "--field-border-top-right-radius": `${isPill ? "9999em" : `min(${syndicator_questions.input_style.border.radius}%, 1.5625rem)`}`,
          "--field-border-bottom-right-radius": `${isPill ? "9999em" : `min(${syndicator_questions.input_style.border.radius}%, 1.5625rem)`}`,
          "--field-border-bottom-left-radius": `${isPill ? "9999em" : `min(${syndicator_questions.input_style.border.radius}%, 1.5625rem)`}`,
        } as SyndicatorQuestionsStyles
      }
    >
      {transformedFields
        .filter((field) => field.name !== "syndicator-question:review_topics")
        .map((field) => {
          const isReviewField = field.name === "syndicator-question:review";
          return (
            <Fragment key={field.name}>
              <FormField
                {...field}
                belowInput={
                  isReviewField ? (
                    <FormReviewTopics
                      id="syndicator-question:review_topics"
                      name="syndicator-question:review_topics"
                      topics={
                        topics?.map((t) => t.display) ??
                        reviewTopicsField?.topics ??
                        []
                      }
                      matchedIndexes={matchedIndexes}
                    />
                  ) : undefined
                }
              />
            </Fragment>
          );
        })}
    </div>
  );
}
